import { ApiError } from "@nabble/sdk";
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { loggedOutState, stateAfterStep } from "./authStateTransitions";
import { fetchAuthState, type AuthState, type AuthStep } from "./session";

type StateUpdate = AuthState | null | ((prev: AuthState | null) => AuthState | null);

export interface AuthStateStore {
  state: AuthState | null;
  loading: boolean;
  /** The latest state, readable from callbacks registered once. */
  stateRef: MutableRefObject<AuthState | null>;
  commit: (next: StateUpdate) => void;
  refreshState: () => Promise<AuthState | null>;
  applyNext: (next: AuthStep, rememberedUntil?: string | null) => void;
}

/** The login step the backend reports, fetched on mount and updated by each step's answer. */
export function useAuthState(): AuthStateStore {
  const [state, setState] = useState<AuthState | null>(null);
  const [loading, setLoading] = useState(true);
  const stateRef = useRef<AuthState | null>(null);

  const commit = useCallback((next: StateUpdate) => {
    setState((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      stateRef.current = value;
      return value;
    });
  }, []);

  const refreshState = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchAuthState();
      commit(next);
      return next;
    } catch (err) {
      // A real 401 means the session is gone — treat it as logged out. Any
      // other failure (429 from the /api/auth/* rate limiter, a network
      // hiccup, a 5xx) is transient: keep whatever auth state we already
      // had instead of bouncing an otherwise-logged-in user to /login.
      if (err instanceof ApiError && err.status === 401) {
        const loggedOut = loggedOutState();
        commit(loggedOut);
        return loggedOut;
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [commit]);

  const applyNext = useCallback(
    (next: AuthStep, rememberedUntil?: string | null) => commit((prev) => stateAfterStep(prev, next, rememberedUntil)),
    [commit],
  );

  useEffect(() => {
    void refreshState();
  }, [refreshState]);

  return { state, loading, stateRef, commit, refreshState, applyNext };
}
